// sentry.js — Initialize Sentry with environment-aware version tags
//
// Prod:     release = app@1.0.5,     tags: app_version=1.0.5
// Non-prod: release = app@dev-a3f9bc2, tags: app_version=dev-a3f9bc2, branch=dev
//
// Import this in your main.tsx BEFORE anything else:
//   import './sentry';

/* global __APP_VERSION__, __COMMIT_SHA__, __BRANCH__ */

import * as Sentry from "@sentry/react";

import { flussonicErrorTracker } from "./layout-components/squawk-player/FlussonicErrorTracker";

// Injected at build time via vite define config
const appVersion =
  typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "unknown";
const commitSha =
  typeof __COMMIT_SHA__ !== "undefined" ? __COMMIT_SHA__ : "unknown";
const branch = typeof __BRANCH__ !== "undefined" ? __BRANCH__ : "unknown";

Sentry.init({
  dsn: "https://6b5d65c1ddfddfb89d10f2f16eb4e490@o4509689222397952.ingest.de.sentry.io/4509689846300752",
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  environment: import.meta.env.VITE_SENTRY_ENV,
  sendDefaultPii: true,
  integrations: [
    Sentry.replayIntegration(),
    Sentry.browserTracingIntegration(),
  ],

  release: `app@${appVersion}`,

  initialScope: {
    tags: {
      app_version: appVersion,
      commit_sha: commitSha,
      branch: branch,
    },
  },

  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  tracesSampleRate: 1.0,

  tracePropagationTargets: [
    "localhost",
    // Calendar API
    /^https:\/\/(www\.)?squawkasaservice\.com\/api\/calendar/,
    // Firehose API
    /^https:\/\/(www\.)?squawkasaservice\.com\/api\/content-feeds/,
    /^https:\/\/(www\.)?squawkasaservice\.com\/api\/dev\/content-feeds/,
    // Headlines API
    /^https:\/\/(www\.)?squawkasaservice\.com\/api\/newsquawk/,
    /^https:\/\/(www\.)?squawkasaservice\.com\/api\/uat\/newsquawk/,
    // Pref Storage API
    /^https:\/\/(www\.)?squawkasaservice\.com\/api\/user/,
    /^https:\/\/(www\.)?squawkasaservice\.com\/api\/dev\/user/,
  ],

  beforeSend(event, hint) {
    // Drop events from bots/crawlers — they produce false errors
    // (Keycloak SSO fails, network errors, etc.)
    if (/HeadlessChrome|PhantomJS|Googlebot|bingbot|Baiduspider/i.test(navigator.userAgent)) {
      return null;
    }

    // Drop errors from browser extensions — not actionable
    const frames = event.exception?.values?.[0]?.stacktrace?.frames || [];
    const hasOnlyExtensionOrSentryFrames = frames.length > 0 && frames.every(
      (f) => !f.in_app || f.filename?.includes("node_modules/@sentry/")
    );
    if (hasOnlyExtensionOrSentryFrames) {
      return null;
    }

    return flussonicErrorTracker.beforeSend(event, hint);
  },
});

export { appVersion, commitSha };
